import { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { ArrowLeft, Mail, MessageCircle, Clock, Send, CheckCircle } from "lucide-react";

interface ContactProps {
  onBackToLanding: () => void;
}

const contactMethods = [
  {
    icon: Mail,
    title: "General Support",
    description: "Get help with your account or technical issues",
    contact: "support@decisionr.xyz",
    responseTime: "Usually within 24 hours"
  },
  {
    icon: MessageCircle,
    title: "Product Feedback",
    description: "Share ideas, feature requests, or suggestions",
    contact: "feedback@decisionr.xyz",
    responseTime: "Usually within 48 hours"
  },
];

const faqs = [
  {
    question: "How do I get started with Decisionr.xyz?",
    answer: "Simply sign up for a free account and start logging your first decision. Our onboarding guide will walk you through the basics."
  },
  {
    question: "Can I export my decision data?",
    answer: "Yes! You can export all your data in CSV or JSON format from the Activity Log page. You own your data completely."
  },
  {
    question: "Is there a mobile app?",
    answer: "Currently we're web-only, but our site works great on mobile browsers. A native mobile app is in our roadmap for 2025."
  },
];

export function Contact({ onBackToLanding }: ContactProps) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, subject, message } = form;
    if (!name || !email || !subject || !message) return;
    const body = encodeURIComponent(`Name: ${name}\n\n${message}`);
    window.location.href = `mailto:support@decisionr.xyz?subject=${encodeURIComponent(subject)}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container py-6">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onBackToLanding}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Button>
            <div className="h-6 w-px bg-border" />
            <div>
              <h1 className="text-2xl font-semibold">Contact Us</h1>
              <p className="text-sm text-muted-foreground">
                Get in touch with our team
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container py-12">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-medium mb-4">We're Here to Help</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Have questions about Decisionr.xyz? Need support? Want to share feedback? 
            We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Methods */}
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-medium mb-8">Get in Touch</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-6 mb-12">
              {contactMethods.map((method, index) => (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <method.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{method.title}</CardTitle>
                        <CardDescription>{method.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="font-medium">{method.contact}</p>
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>{method.responseTime}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Contact Form */}
            <Card>
              <CardHeader>
                <CardTitle>Send us a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you as soon as possible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-6 space-y-2">
                    <CheckCircle className="h-8 w-8 text-green-500 mx-auto" />
                    <p className="font-medium">Your email client has been opened.</p>
                    <p className="text-sm text-muted-foreground">Complete the send there, or email us directly at support@decisionr.xyz.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium mb-2">Name</label>
                        <Input id="name" placeholder="Your name" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-2">Email</label>
                        <Input id="email" type="email" placeholder="your@email.com" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium mb-2">Subject</label>
                      <Input id="subject" placeholder="What's this about?" required value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2">Message</label>
                      <Textarea
                        id="message"
                        rows={5}
                        placeholder="Tell us more about your question or feedback..."
                        required
                        value={form.message}
                        onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      />
                    </div>
                    <Button type="submit" className="w-full">
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Company Info */}
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">

                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">Support Hours</p>
                    <p className="text-sm text-muted-foreground">
                      Monday - Friday: Support provided on as-available basis
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick FAQ */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Answers</CardTitle>
                <CardDescription>
                  Common questions we receive
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div key={index}>
                      <h4 className="font-medium text-sm mb-1">{faq.question}</h4>
                      <p className="text-sm text-muted-foreground">{faq.answer}</p>
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="w-full mt-4">
                  View All FAQs
                </Button>
              </CardContent>
            </Card>

            {/* Status */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-2"></div>
                <p className="font-medium text-green-800">All Systems Operational</p>
                <p className="text-sm text-green-600">Last updated: Just now</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <footer className="border-t mt-16">
        <div className="container py-8">
          <div className="text-center text-sm text-muted-foreground">
            <p>© 2025 Decisionr.xyz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}